import type { ActivityEvent, ActivityDigest } from "@/hooks/useProjectActivity";

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export type ActivityCategory = "development" | "pipeline" | "costs";

const EVENT_TYPE_TO_CATEGORY: Record<string, ActivityCategory> = {
  work_lifecycle: "development",
  session_topic: "development",
  session_closed: "development",
  session_started: "development",
  pipeline_phase_completed: "pipeline",
  phase_finish: "pipeline",
  token_usage_daily: "costs",
  story_cost_summary: "costs",
  token_usage: "costs",
};

export function categorize(eventType: string): ActivityCategory {
  return EVENT_TYPE_TO_CATEGORY[eventType] ?? "development";
}

export const CATEGORY_META: Record<
  ActivityCategory,
  { label: string; colorClass: string; bgClass: string; borderClass: string }
> = {
  development: {
    label: "Development",
    colorClass: "text-blue-400",
    bgClass: "bg-blue-500/10",
    borderClass: "border-blue-500/20",
  },
  pipeline: {
    label: "Pipeline",
    colorClass: "text-emerald-400",
    bgClass: "bg-emerald-500/10",
    borderClass: "border-emerald-500/20",
  },
  costs: {
    label: "Costs",
    colorClass: "text-amber-400",
    bgClass: "bg-amber-500/10",
    borderClass: "border-amber-500/20",
  },
};

// ---------------------------------------------------------------------------
// Digest summary metrics
// ---------------------------------------------------------------------------

export interface DigestSummary {
  totalEvents: number;
  storiesActive: number;
  sessionsToday: number;
  pipelinePhases: number;
}

export function computeDigestSummary(
  digest: ActivityDigest | null,
  events: ActivityEvent[],
  total: number,
): DigestSummary {
  if (digest) {
    return {
      totalEvents: total,
      storiesActive: Object.values(digest.workflow).reduce((a, b) => a + b, 0),
      sessionsToday: Object.values(digest.sessions).reduce((a, b) => a + b, 0),
      pipelinePhases: Object.values(digest.pipeline).reduce((a, b) => a + b, 0),
    };
  }

  return {
    totalEvents: total || events.length,
    storiesActive: events.filter((e) => e.event_type === "work_lifecycle").length,
    sessionsToday: events.filter(
      (e) => e.event_type === "session_topic" || e.event_type === "session_closed",
    ).length,
    pipelinePhases: events.filter(
      (e) => e.event_type === "pipeline_phase_completed" || e.event_type === "phase_finish",
    ).length,
  };
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function formatCost(usd: number): string {
  if (usd >= 0.01) return `$${usd.toFixed(2)}`;
  return `$${usd.toFixed(3)}`;
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.round((seconds % 3600) / 60);
  return mins > 0 ? `${String(hours)}h ${String(mins)}m` : `${String(hours)}h`;
}

// ---------------------------------------------------------------------------
// Type–Subtype derivation (C1: actor_kind, duration_seconds, governance_audit)
// ---------------------------------------------------------------------------

export type TypeSubtypeResult = {
  type: string;
  subtype: string;
  /** String key — component maps this to its Lucide icon; utils stay lucide-free */
  iconKey: string;
  colorClass: string;
};

export function deriveTypeSubtype(
  event: Pick<ActivityEvent, "event_type" | "payload">,
): TypeSubtypeResult {
  const { event_type, payload } = event;

  switch (event_type) {
    case "work_lifecycle": {
      const phase = payload?.phase ?? "";
      const ev = payload?.event ?? "";
      const evHuman =
        ev === "start" ? "inició" : ev === "complete" ? "terminó" : ev;
      return {
        type: "Story",
        subtype: [phase, evHuman].filter(Boolean).join(" · "),
        iconKey: "GitBranch",
        colorClass: "text-blue-400",
      };
    }
    case "pipeline_phase_completed":
    case "phase_finish": {
      const pipeline = payload?.pipeline_name ?? "";
      const phase = payload?.phase ?? "";
      const label =
        pipeline && phase
          ? `${pipeline} · ${phase} terminó`
          : pipeline || phase || "terminó";
      return { type: "Pipeline", subtype: label, iconKey: "FileCheck", colorClass: "text-purple-400" };
    }
    case "governance_audit": {
      const subject = (payload?.subject_id ?? "").replace(/^gate-/, "");
      const decision = payload?.decision ?? "";
      const isPass = decision === "pass";
      return {
        type: "Gate",
        subtype: [subject, decision].filter(Boolean).join(" · "),
        iconKey: isPass ? "Shield" : "AlertTriangle",
        colorClass: isPass ? "text-emerald-400" : "text-red-400",
      };
    }
    case "rubric_verdict": {
      const skill = payload?.skill ?? "";
      const verdict = payload?.verdict ?? "";
      return {
        type: "Rúbrica",
        subtype: [skill, verdict, "self-check"].filter(Boolean).join(" · "),
        iconKey: "CheckCircle",
        colorClass: "text-emerald-400",
      };
    }
    case "token_usage_daily":
    case "token_usage": {
      const model = payload?.by_model ? (Object.keys(payload.by_model)[0] ?? "") : "";
      return { type: "Costo", subtype: model, iconKey: "DollarSign", colorClass: "text-amber-400" };
    }
    case "story_cost_summary": {
      const key = payload?.jira_key ?? "";
      return {
        type: "Costo",
        subtype: key ? `${key} · resumen` : "resumen",
        iconKey: "DollarSign",
        colorClass: "text-amber-400",
      };
    }
    case "session_started":
      return { type: "Sesión", subtype: "inició", iconKey: "Play", colorClass: "text-muted-foreground" };
    case "session_closed":
      return { type: "Sesión", subtype: "cerró", iconKey: "LogOut", colorClass: "text-muted-foreground" };
    case "session_topic":
      return { type: "Sesión", subtype: "tema", iconKey: "MessageSquare", colorClass: "text-muted-foreground" };
    default: {
      const humanized = event_type.replace(/[-_]/g, " ");
      return { type: "Evento", subtype: humanized, iconKey: "Activity", colorClass: "text-muted-foreground" };
    }
  }
}

export function isGateEvent(event: Pick<ActivityEvent, "event_type">): boolean {
  return event.event_type === "governance_audit";
}

export function formatCostOrDash(usd: number | null | undefined): string {
  if (usd == null) return "—";
  return formatCost(usd);
}

export function formatDurationOrNA(seconds: number | null | undefined): string {
  if (seconds == null) return "NA";
  return formatDuration(seconds);
}

export function actorBadgeKind(
  event: Pick<ActivityEvent, "actor_kind"> & { payload?: { actor?: string } },
): "agent" | null {
  if (event.actor_kind === "agent") return "agent";
  if (event.payload?.actor === "agent") return "agent";
  return null;
}
